import type { FinalizePayload, GuestInvitationPayload, ResendGuestInvitationPayload } from './useFetchContractBuilder'

import { useQueryClientUtils } from '../../useQueryClientUtils'
import { useFetchContractBuilder } from './useFetchContractBuilder'
import { queryKeys as contractBuilderQueryKeys } from './useQueryContractBuilder'

const refreshParticipantToken = () => {
  const { refreshToken, join: _join } = useFetchContractBuilder()
  const queryClient = useQueryClient()
  const { invalidateQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: refreshToken,
    onSuccess(data, variables) {
      const currentData = queryClient.getQueryData<Awaited<ReturnType<typeof _join>>>(
        contractBuilderQueryKeys.session({
          contractId: variables.contractId,
        }).queryKey,
      )

      // If session found we mutate it, otherwise we do nothing.
      if (!currentData)
        return

      const dataToUpdate = {
        ...currentData.data,
        participant: data.data,
      }

      queryClient.setQueryData(contractBuilderQueryKeys.session({
        contractId: variables.contractId,
      }).queryKey, { data: dataToUpdate })
    },
    onError(_, variables) {
      invalidateQueries([
        contractBuilderQueryKeys.session({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
  })
}

const addGuest = () => {
  const { addGuest: _addGuest, getGuests: _getGuests } = useFetchContractBuilder()
  const queryClient = useQueryClient()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()
  const { activeWorkspaceId } = useAuth()

  return useMutation({
    mutationFn: ({ contractId, payload }: { contractId: string, payload: GuestInvitationPayload }) =>
      _addGuest({ contractId, workspaceId: unref(activeWorkspaceId) || '', payload }),
    onMutate: (variables) => {
      cancelQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
    onSuccess: (data, variables) => {
      const currentGuests = queryClient.getQueryData<Awaited<ReturnType<typeof _getGuests>>>(
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      )

      // Without a loaded list there is nothing to merge into, so fetch the full list instead.
      if (!currentGuests) {
        invalidateQueries([
          contractBuilderQueryKeys.guests({
            contractId: variables.contractId,
          }).queryKey,
        ])
        return
      }

      const guests = currentGuests.data
      const newGuests = guests.some(guest => guest.id === data.data.id)
        ? guests.map(guest => guest.id === data.data.id ? data.data : guest)
        : guests.concat(data.data)

      queryClient.setQueryData(contractBuilderQueryKeys.guests({
        contractId: variables.contractId,
      }).queryKey, { data: newGuests })
    },
    onError: (_err, variables) => {
      invalidateQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
  })
}

const deleteGuest = () => {
  const { deleteGuest: _deleteGuest } = useFetchContractBuilder()
  const { activeWorkspaceId } = useAuth()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: ({ contractId, participantId }: { contractId: string, participantId: string }) =>
      _deleteGuest({ contractId, workspaceId: unref(activeWorkspaceId) || '', participantId }),
    onMutate: (variables) => {
      cancelQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
    onSettled: (_, __, variables) => {
      invalidateQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
  })
}

const resendGuestInvitation = () => {
  const { resendGuestInvitation: _resendGuestInvitation, getGuests: _getGuests } = useFetchContractBuilder()
  const { activeWorkspaceId } = useAuth()
  const queryClient = useQueryClient()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: ({ contractId, participantId, payload }: { contractId: string, participantId: string, payload: ResendGuestInvitationPayload }) =>
      _resendGuestInvitation({ contractId, participantId, workspaceId: unref(activeWorkspaceId) || '', payload }),
    onMutate: (variables) => {
      cancelQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
    onSuccess: (data, variables) => {
      const currentGuests = queryClient.getQueryData<Awaited<ReturnType<typeof _getGuests>>>(
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      )

      const guests = currentGuests?.data ?? []
      const newGuests = guests.map(guest => guest.id === data.data.id ? data.data : guest)

      queryClient.setQueryData(contractBuilderQueryKeys.guests({
        contractId: variables.contractId,
      }).queryKey, { data: newGuests })
    },
    onError: (_, variables) => {
      invalidateQueries([
        contractBuilderQueryKeys.guests({
          contractId: variables.contractId,
        }).queryKey,
      ])
    },
  })
}

const finalize = () => {
  const { finalize: _finalize } = useFetchContractBuilder()
  const { activeWorkspaceId } = useAuth()
  const { cancelRelationalRequestsByType, invalidateRelationalCacheDataByType } = useQueryClientUtils()

  return useMutation({
    mutationFn: ({ contractId, payload }: { contractId: string, payload: FinalizePayload }) =>
      _finalize({ contractId, workspaceId: unref(activeWorkspaceId) || '', payload }),
    onMutate: () => {
      cancelRelationalRequestsByType('contract')
    },
    onSettled: () => {
      invalidateRelationalCacheDataByType('contract')
    },
  })
}

export const useMutationContractBuilder = {
  refreshParticipantToken,
  finalize,
  addGuest,
  deleteGuest,
  resendGuestInvitation,
}
